import React, { FC, Fragment, useState } from 'react';
import { IconButton, TableRow, TableCell, Typography } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import NotesIcon from '@material-ui/icons/Notes';
import classNames from 'classnames';

import { LeaderboardScore } from 'api';
import { CustomSVG, UserAvatar } from 'components';
import { getFullName } from 'helpers';
import { useCurrentUser } from 'hooks';

import UserPointDialog from './UserPointDialog';

interface IProps extends WithStyles<typeof styles> {
  row: LeaderboardScore;
}

const styles = (theme: Theme) =>
  createStyles({
    row: {
      height: 82, // Magic number from design
    },
    currentUser: {
      border: `1px solid ${theme.palette.secondary.main}`,
    },
    firstCell: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    changeCell: {
      display: 'inline-flex',
      alignItems: 'center',
    },
    changeIcon: {
      marginRight: theme.spacing(0.5),
    },
    lastCell: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(3),
    },
  });

const LeaderboardTableRow: FC<IProps> = ({ classes, row }) => {
  const [open, setOpen] = useState(false);
  const currentUser = useCurrentUser();

  const { user_id, ranking, position_change, user, score } = row;
  const { job_title } = user;

  const rowClasses = classNames(classes.row, {
    [classes.currentUser]: currentUser.id === user_id,
  });

  function getRanking(currentRanking: LeaderboardScore['ranking']) {
    return <Typography variant="subtitle2">{currentRanking}</Typography>;
  }

  function getChange(positionChange: LeaderboardScore['position_change']) {
    if (positionChange === 0) {
      return '--';
    }

    if (positionChange > 0) {
      return (
        <Fragment>
          <CustomSVG className={classes.changeIcon} color="secondary" name="arrowUp" />
          <Typography color="secondary">{positionChange}</Typography>
        </Fragment>
      );
    }

    return (
      <Fragment>
        <CustomSVG className={classes.changeIcon} color="disabled" name="arrowDown" />
        <Typography color="textSecondary">{positionChange}</Typography>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <UserPointDialog
        leaderboardScore={row}
        renderRank={getRanking}
        renderChange={getChange}
        open={open}
        onClose={() => setOpen(false)}
      />
      <TableRow key={user_id} className={rowClasses}>
        <TableCell align="center" className={classes.firstCell} padding="none">
          {getRanking(ranking)}
        </TableCell>
        <TableCell align="center" className={classes.firstCell} padding="none">
          <div className={classes.changeCell}>{getChange(position_change)}</div>
        </TableCell>
        <TableCell className={classes.firstCell} padding="none">
          <UserAvatar link size={50} user={user} />
        </TableCell>
        <TableCell>{getFullName(user)}</TableCell>
        <TableCell>{job_title}</TableCell>
        <TableCell>{score}</TableCell>
        <TableCell className={classes.lastCell}>
          <IconButton onClick={() => setOpen(true)}>
            <NotesIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

export default withStyles(styles)(LeaderboardTableRow);
